import React, { useEffect, useState } from "react";

import { useDispatch, useSelector } from "react-redux";
import { RootState } from "@/store/store";

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@/uiComponents/components/ui/table";

import { Button } from "@/uiComponents/components/ui/button";
import { IconPlus } from "@tabler/icons-react";

import { deleteBranchApi, getBranchApi } from "@/modules/organizationServices";

import {
  deleteBranch,
  setBranches,
} from "@/store/redux/organization/branchSlice";

import { EllipsisVertical, SquarePen, Trash2 } from "lucide-react";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@radix-ui/react-dropdown-menu";
import AddBranch from "./AddBranch";
import UpdateBranch from "./UpdateBranch";
import { Branch } from "@/types/organization";

import { ErrorHelper } from "@/utils/errorHelper";
import { BranchError } from "@/enums/errorMessages";
import DeleteModal from "../DeleteModal";
import { toastMessage } from "@/utils/toastHelper";
import { AddExpenseButton } from "@/utils/AddExpenseButton";

export default function Branches() {
  const dispatch = useDispatch();
  const branches = useSelector((state: RootState) => state.branch.branches);
  const userDetails = useSelector(
    (state: RootState) => state.userDetail.userDetail
  );
  const role = useSelector((state: RootState) => state.userDetail.role);
  const orgId = userDetails?.organizationId;
  const [isAddOpen, setIsAddOpen] = useState<boolean>(false);
  const [isUpdateOpen, setIsUpdateOpen] = useState<boolean>(false);
  const [selectedBranch, setSelectedBranch] = useState<Branch | null>(null);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const handleDeleteBranch = async (id: number) => {
    try {
      await deleteBranchApi(id, role);
      dispatch(deleteBranch(id));
      toastMessage("success", BranchError.BRANCH_DELETE);
      getBranches();
    } catch (error) {
      const actualError = ErrorHelper(error);
      const message = Array.isArray(actualError) ? actualError[0] : actualError;
      toastMessage("error", message);
    }
  };

  const getBranches = async () => {
    try {
      if (!orgId) return;
      const res = await getBranchApi(orgId);
      const response = res.branches;
      if (response !== undefined) {
        const branches = Array.isArray(response) ? response : [response];
        dispatch(setBranches(branches));
      }
    } catch (error) {
      const actualError = ErrorHelper(error);
      const message = Array.isArray(actualError) ? actualError[0] : actualError;
      toastMessage("error", message);
    }
  };

  useEffect(() => {
    getBranches();
  }, []);

  return (
    <div className="flex flex-1 flex-col max-w-screen md:ml-4 px-4 ">
      {(role.isSuperAdmin || role.isOrgAdmin) && (
        <div>
          <h2 className="text-2xl  font-semibold w-full">Branches</h2>

          <AddBranch onClose={() => setIsAddOpen(false)} isOpen={isAddOpen} />

          {selectedBranch && (
            <UpdateBranch
              onClose={() => setIsUpdateOpen(false)}
              isOpen={isUpdateOpen}
              branch={selectedBranch}
            />
          )}
        </div>
      )}
      <AddExpenseButton />

      <div className="overflow-x-auto max-w-4xl mt-6 rounded-md ">
        <Table className="min-w-full bg-white border-1 ">
          {(role.isSuperAdmin || role.isOrgAdmin) && (
            <TableRow>
              <TableHead className="text-right" colSpan={4}>
                <Button
                  className="bg-[#191F29]  hover:bg-[#2A313D] hover:text-white active:bg-[#001533] text-white  cursor-pointer  whitespace-nowrap mt-2 mb-2  "
                  onClick={() => setIsAddOpen(true)}
                  variant="outline"
                  size="sm"
                >
                  <IconPlus />
                  <span className="hidden lg:inline ml-1">Add Branch</span>
                </Button>
              </TableHead>
            </TableRow>
          )}

          <TableBody>
            {branches.map((branch) => (
              <TableRow key={branch.id}>
                <TableCell className="font-medium">
                  {branch.branchName}
                </TableCell>
                {(role.isSuperAdmin || role.isOrgAdmin) && (
                  <TableCell colSpan={10} className="text-right pr-4">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="ghost"
                          className="h-10 w-10 p-0 cursor-pointer hover:bg-gray-200 "
                        >
                          <span className="sr-only">Open menu</span>
                          <EllipsisVertical className=" text-gray-700 text-xl  " />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent
                        align="end"
                        className="w-32 rounded-md shadow-lg border bg-white"
                      >
                        <DropdownMenuItem
                          onClick={() => {
                            setIsUpdateOpen(true);
                            setSelectedBranch(branch);
                          }}
                          className="cursor-pointer text-left hover:bg-gray-300 text-sm text-gray-800 px-3 py-2"
                        >
                          <div className="flex gap-2">
                            <SquarePen size={16} /> Edit
                          </div>
                        </DropdownMenuItem>
                        <div className="h-px bg-gray-200 my-0.5 w-full" />
                        <DropdownMenuItem
                          onClick={() => {
                            setIsDeleteOpen(true);
                            setSelectedBranch(branch);
                          }}
                          className="cursor-pointer text-left hover:bg-gray-300 text-sm text-gray-800 px-3 py-2"
                        >
                          <div className="flex gap-2">
                            <Trash2 size={16} /> Delete
                          </div>
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {selectedBranch && (
          <DeleteModal
            isOpen={isDeleteOpen}
            onClose={() => setIsDeleteOpen(false)}
            item={selectedBranch}
            onConfirm={(item) => {
              handleDeleteBranch(item.id);
              setIsDeleteOpen(false);
              setSelectedBranch(null);
            }}
            itemLabel="branch"
          />
        )}
      </div>
    </div>
  );
}
